import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Helmet } from 'react-helmet-async';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Download, CalendarIcon, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

import { OverviewCards } from '@/components/analytics/OverviewCards';
import { PerformanceChart } from '@/components/analytics/PerformanceChart';
import { FunnelVisualization } from '@/components/analytics/FunnelVisualization';
import { QuestionAnalytics } from '@/components/analytics/QuestionAnalytics';
import { ResponseTable } from '@/components/analytics/ResponseTable';
import { TrafficSources } from '@/components/analytics/TrafficSources';
import { DeviceAnalytics } from '@/components/analytics/DeviceAnalytics';
import { AIInsightsCard } from '@/components/analytics/AIInsightsCard';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { logStructuredError } from '@/lib/aiInsightsNormalizer';
import {
  buildFunnelSteps,
  buildResponseRows,
  buildQuestionStats,
  buildChartData,
  buildTrafficData,
  buildDeviceData,
  type RawFormViewRecord,
  type RawFormSubmissionRecord,
  type RawAnalyticsForm,
} from '@/lib/analyticsNormalizer';

type TimeRange = 'today' | '7d' | '30d' | 'custom';

function getDateRange(range: TimeRange, customStart?: Date, customEnd?: Date) {
  const end = new Date();
  const start = new Date();
  switch (range) {
    case 'today':
      start.setHours(0, 0, 0, 0);
      break;
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case 'custom':
      if (customStart && customEnd) return { start: customStart, end: customEnd };
      start.setDate(start.getDate() - 7);
      break;
  }
  return { start, end };
}

function getPreviousPeriod(start: Date, end: Date) {
  const diff = end.getTime() - start.getTime();
  return { start: new Date(start.getTime() - diff), end: new Date(start.getTime()) };
}

const Analytics = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [customStart, setCustomStart] = useState<Date | undefined>();
  const [customEnd, setCustomEnd] = useState<Date | undefined>();
  const [selectedFormId, setSelectedFormId] = useState<string>('all');
  const [isRefreshingAI, setIsRefreshingAI] = useState(false);
  const { toast } = useToast();
  const { hasAIInsights } = usePlanLimits();

  // Raw data — typed against the raw record shapes from the normalizer
  const [forms, setForms] = useState<RawAnalyticsForm[]>([]);
  const [views, setViews] = useState<RawFormViewRecord[]>([]);
  const [prevViews, setPrevViews] = useState<RawFormViewRecord[]>([]);
  const [submissions, setSubmissions] = useState<RawFormSubmissionRecord[]>([]);
  const [prevSubmissions, setPrevSubmissions] = useState<RawFormSubmissionRecord[]>([]);

  const { start, end } = getDateRange(timeRange, customStart, customEnd);
  const prev = getPreviousPeriod(start, end);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [formsRes, viewsRes, prevViewsRes, subsRes, prevSubsRes] = await Promise.all([
          supabase
            .from('forms')
            .select('id, title, fields, is_published, ai_insights, ai_summary')
            .eq('user_id', user.id),
          supabase
            .from('form_views')
            .select('id, viewed_at, device_type, referrer, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('viewed_at', start.toISOString())
            .lte('viewed_at', end.toISOString()),
          supabase
            .from('form_views')
            .select('id, viewed_at, device_type, referrer, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('viewed_at', prev.start.toISOString())
            .lt('viewed_at', prev.end.toISOString()),
          supabase
            .from('submissions')
            .select('id, submitted_at, completion_time, device_type, data, form_id, forms!inner(user_id, id, title, fields)')
            .eq('forms.user_id', user.id)
            .gte('submitted_at', start.toISOString())
            .lte('submitted_at', end.toISOString())
            .order('submitted_at', { ascending: false }),
          supabase
            .from('submissions')
            .select('id, submitted_at, completion_time, device_type, form_id, forms!inner(user_id)')
            .eq('forms.user_id', user.id)
            .gte('submitted_at', prev.start.toISOString())
            .lt('submitted_at', prev.end.toISOString()),
        ]);

        setForms((formsRes.data as RawAnalyticsForm[]) ?? []);
        setViews((viewsRes.data as unknown as RawFormViewRecord[]) ?? []);
        setPrevViews((prevViewsRes.data as unknown as RawFormViewRecord[]) ?? []);
        setSubmissions((subsRes.data as unknown as RawFormSubmissionRecord[]) ?? []);
        setPrevSubmissions((prevSubsRes.data as unknown as RawFormSubmissionRecord[]) ?? []);
      } catch (e) {
        logStructuredError(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, timeRange, customStart?.getTime(), customEnd?.getTime()]);

  // ---------------------------------------------------------------------------
  // Derived / filtered raw data
  // ---------------------------------------------------------------------------
  const filteredViews = selectedFormId === 'all'
    ? views
    : views.filter((v) => v.form_id === selectedFormId);
  const filteredPrevViews = selectedFormId === 'all'
    ? prevViews
    : prevViews.filter((v) => v.form_id === selectedFormId);
  const filteredSubmissions = selectedFormId === 'all'
    ? submissions
    : submissions.filter((s) => s.form_id === selectedFormId);
  const filteredPrevSubmissions = selectedFormId === 'all'
    ? prevSubmissions
    : prevSubmissions.filter((s) => s.form_id === selectedFormId);

  const selectedForms = selectedFormId === 'all'
    ? forms
    : forms.filter((f) => f.id === selectedFormId);

  // ---------------------------------------------------------------------------
  // Aggregate metrics (scalars — safe because array lengths are always numbers)
  // ---------------------------------------------------------------------------
  const totalViews = filteredViews.length;
  const responses = filteredSubmissions.length;
  const formStarts = Math.round(totalViews * 0.65);
  const completionRate = formStarts > 0 ? Math.round((responses / formStarts) * 100) : 0;
  const conversionRate = totalViews > 0 ? Math.round((responses / totalViews) * 100) : 0;
  const completionTimes = filteredSubmissions
    .filter((s) => typeof s.completion_time === 'number')
    .map((s) => s.completion_time as number);
  const avgCompletionTime =
    completionTimes.length > 0
      ? Math.round(completionTimes.reduce((a, b) => a + b, 0) / completionTimes.length)
      : 0;

  const prevTotal = filteredPrevViews.length;
  const prevResp = filteredPrevSubmissions.length;
  const prevStarts = Math.round(prevTotal * 0.65);
  const prevCompRate = prevStarts > 0 ? Math.round((prevResp / prevStarts) * 100) : 0;
  const prevConvRate = prevTotal > 0 ? Math.round((prevResp / prevTotal) * 100) : 0;
  const prevCT = filteredPrevSubmissions
    .filter((s) => typeof s.completion_time === 'number')
    .map((s) => s.completion_time as number);
  const prevAvgCT =
    prevCT.length > 0
      ? Math.round(prevCT.reduce((a, b) => a + b, 0) / prevCT.length)
      : 0;

  // ---------------------------------------------------------------------------
  // Normalized component data — produced by the normalizer, never raw records
  // ---------------------------------------------------------------------------
  const funnelSteps = buildFunnelSteps(totalViews, formStarts, responses);
  const responseRows = buildResponseRows(filteredSubmissions);
  const questionStats = buildQuestionStats(selectedForms, filteredSubmissions);
  const chartData = buildChartData(filteredViews, filteredSubmissions, start, end);
  const trafficData = buildTrafficData(filteredViews);
  const deviceData = buildDeviceData(filteredViews);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleExportCSV = () => {
    if (filteredSubmissions.length === 0) {
      toast({ title: 'No data to export', variant: 'destructive' });
      return;
    }
    const headers = ['Submission ID', 'Submitted At', 'Completion Time (s)', 'Device Type'];
    const rows = filteredSubmissions.map((s) => [
      s.id,
      s.submitted_at,
      s.completion_time ?? '',
      s.device_type ?? '',
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-export-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading Analytics Engine...</p>
        </div>
      </DashboardLayout>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <DashboardLayout>
      <Helmet>
        <title>Analytics | Bravax Forms</title>
      </Helmet>

      <div className="space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
            <p className="text-muted-foreground text-sm">
              Deep insights into your form performance and submissions.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Form Filter */}
            <Select value={selectedFormId} onValueChange={setSelectedFormId}>
              <SelectTrigger className="w-[200px] bg-card">
                <SelectValue placeholder="All Forms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Forms ({forms.length})</SelectItem>
                {forms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Time Range */}
            <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
              <SelectTrigger className="w-[140px] bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>

            {/* Custom Date Pickers */}
            {timeRange === 'custom' && (
              <div className="flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn('gap-2', !customStart && 'text-muted-foreground')}
                    >
                      <CalendarIcon className="h-4 w-4" />
                      {customStart ? format(customStart, 'PP') : 'Start Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar mode="single" selected={customStart} onSelect={setCustomStart} />
                  </PopoverContent>
                </Popover>
                <span className="text-xs text-muted-foreground">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn('gap-2', !customEnd && 'text-muted-foreground')}
                    >
                      <CalendarIcon className="h-4 w-4" />
                      {customEnd ? format(customEnd, 'PP') : 'End Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar mode="single" selected={customEnd} onSelect={setCustomEnd} />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Overview Stat Cards */}
        <OverviewCards
          totalViews={totalViews}
          formStarts={formStarts}
          responses={responses}
          completionRate={completionRate}
          conversionRate={conversionRate}
          avgCompletionTime={avgCompletionTime}
          prevTotalViews={prevTotal}
          prevFormStarts={prevStarts}
          prevResponses={prevResp}
          prevCompletionRate={prevCompRate}
          prevConversionRate={prevConvRate}
          prevAvgCompletionTime={prevAvgCT}
        />

        {/* Main Performance Chart */}
        <PerformanceChart data={chartData} />

        {/* Middle Section: Funnel + Response Table */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            {/* FunnelVisualization receives steps: FunnelStep[] — correct contract */}
            <FunnelVisualization steps={funnelSteps} />
          </div>
          <div className="lg:col-span-2">
            {/* ResponseTable receives responses: ResponseRow[] — correct contract */}
            <ResponseTable responses={responseRows} />
          </div>
        </div>

        {/* Question Analytics */}
        {/* QuestionAnalytics receives questions: QuestionStat[] — correct contract */}
        <QuestionAnalytics questions={questionStats} />

        {/* AI Insights Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="text-xl font-bold">AI Response Intelligence</h2>
            </div>
            {!hasAIInsights && (
              <span className="text-xs font-semibold text-amber-500 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                PRO FEATURE
              </span>
            )}
          </div>

          {!hasAIInsights ? (
            <Card className="card-elegant border-amber-500/20 bg-gradient-to-r from-amber-500/5 via-transparent to-transparent">
              <CardContent className="text-center pb-8 pt-8">
                <div className="flex flex-col items-center gap-4">
                  <p className="text-sm text-muted-foreground italic">
                    &ldquo;Automatically summarize 1,000+ responses in seconds.&rdquo;
                  </p>
                  <Button
                    onClick={() => (window.location.href = '/billing-new')}
                    className="bg-amber-600 hover:bg-amber-700 text-white min-w-[200px] h-11"
                  >
                    Upgrade to Pro
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <AIInsightsCard
              formTitle={
                selectedFormId === 'all'
                  ? 'All Forms'
                  : forms.find((f) => f.id === selectedFormId)?.title ?? 'Selected Form'
              }
              insights={
                selectedFormId === 'all'
                  ? null
                  : (forms.find((f) => f.id === selectedFormId)?.ai_insights ?? null)
              }
              isRefreshing={isRefreshingAI}
              onRefresh={async () => {
                if (selectedFormId === 'all') {
                  toast({
                    title: 'Select a form',
                    description: 'Please select a specific form to generate AI insights.',
                  });
                  return;
                }

                setIsRefreshingAI(true);
                try {
                  const { data: invokeData, error } = await supabase.functions.invoke(
                    'ai-summarize-form',
                    { body: { formId: selectedFormId } }
                  );

                  if (error) throw error;
                  if (invokeData && invokeData.success === false) {
                    throw new Error(invokeData.message ?? invokeData.error ?? 'AI analysis failed');
                  }

                  const { data: updatedForm, error: fetchError } = await supabase
                    .from('forms')
                    .select('ai_insights, ai_summary')
                    .eq('id', selectedFormId)
                    .single();

                  if (fetchError) throw fetchError;

                  setForms((prev) =>
                    prev.map((f) => (f.id === selectedFormId ? { ...f, ...updatedForm } : f))
                  );

                  toast({
                    title: 'AI Analysis Complete',
                    description: 'Your form insights have been updated.',
                  });
                } catch (err: unknown) {
                  const errObj = err as { message?: string };
                  logStructuredError(err);
                  toast({
                    title: 'Analysis Failed',
                    description: errObj.message ?? 'Failed to generate AI insights.',
                    variant: 'destructive',
                  });
                } finally {
                  setIsRefreshingAI(false);
                }
              }}
            />
          )}
        </div>

        {/* Traffic + Devices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrafficSources data={trafficData} />
          <DeviceAnalytics data={deviceData} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
